import apiClient from './apiClient';
import {
  Vehicle,
  CreateVehicleDto,
  UpdateVehicleDto,
  VehicleFilters
} from '../../types/model/vehicle.model';

/**
 * Servicio de API para Vehículos
 * Maneja todas las operaciones CRUD de vehículos
 */

/**
 * Respuesta estándar de la API
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export const vehicleService = {
  /**
   * Obtiene todos los vehículos con filtros opcionales
   * @param filters - Filtros de búsqueda
   * @returns Lista de vehículos
   */
  getAll: async (filters?: VehicleFilters): Promise<Vehicle[]> => {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }

    const response = await apiClient.get<ApiResponse<Vehicle[]>>(
      `/vehicles${params.toString() ? `?${params.toString()}` : ''}`
    );

    return response.data.data;
  },

  /**
   * Obtiene un vehículo por su ID
   * @param id - ID del vehículo
   * @returns Vehículo encontrado
   */
  getById: async (id: string): Promise<Vehicle> => {
    const response = await apiClient.get<ApiResponse<Vehicle>>(`/vehicles/${id}`);
    return response.data.data;
  },

  /**
   * Crea un nuevo vehículo
   * @param data - Datos del vehículo
   * @returns Vehículo creado
   */
  create: async (data: CreateVehicleDto): Promise<Vehicle> => {
    const response = await apiClient.post<ApiResponse<Vehicle>>('/vehicles', data);
    return response.data.data;
  },

  /**
   * Actualiza un vehículo existente
   * @param id - ID del vehículo
   * @param data - Datos a actualizar
   * @returns Vehículo actualizado
   */
  update: async (id: string, data: UpdateVehicleDto): Promise<Vehicle> => {
    const response = await apiClient.put<ApiResponse<Vehicle>>(`/vehicles/${id}`, data);
    return response.data.data;
  },

  /**
   * Elimina un vehículo
   * @param id - ID del vehículo
   * @returns true si se eliminó correctamente
   */
  delete: async (id: string): Promise<boolean> => {
    await apiClient.delete(`/vehicles/${id}`);
    return true;
  },

  /**
   * Obtiene vehículos disponibles (atajo)
   * @returns Lista de vehículos disponibles
   */
  getAvailable: async (): Promise<Vehicle[]> => {
    return vehicleService.getAll({ status: 'AVAILABLE' } as any);
  },

  /**
   * Obtiene vehículos vendidos (atajo)
   * @returns Lista de vehículos vendidos
   */
  getSold: async (): Promise<Vehicle[]> => {
    return vehicleService.getAll({ status: 'SOLD' } as any);
  },
};

export default vehicleService;
